import React, { useEffect, useState } from 'react'
import {
  Box, Typography, Stack, Paper, TextField, Button, Switch, FormControlLabel,
  Alert, RadioGroup, Radio, Chip, CircularProgress, Snackbar, Divider, Tabs, Tab,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Tooltip,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import VisibilityIcon from '@mui/icons-material/Visibility'
import RefreshIcon from '@mui/icons-material/Refresh'
import { apiService } from '../services/api'

const AdminEmailSettingsPage = () => {
  const [tab, setTab] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [snack, setSnack] = useState(null)

  // Settings
  const [settings, setSettings] = useState(null)
  const [mode, setMode] = useState('test')
  const [testRecipient, setTestRecipient] = useState('')
  const [ccAdmins, setCcAdmins] = useState(true)
  const [extraCc, setExtraCc] = useState('')
  const [notes, setNotes] = useState('')

  // Log
  const [log, setLog] = useState([])

  const loadSettings = async () => {
    setLoading(true); setError(null)
    try {
      const resp = await apiService.getEmailSettings()
      setSettings(resp.data)
      setMode(resp.data.mode)
      setTestRecipient(resp.data.test_recipient || '')
      setCcAdmins(!!resp.data.cc_admins)
      setExtraCc(resp.data.extra_cc || '')
      setNotes(resp.data.notes || '')
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    } finally { setLoading(false) }
  }

  const loadLog = async () => {
    try {
      const resp = await apiService.getEmailLog({ limit: 200 })
      setLog(resp.data.items || [])
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    }
  }

  useEffect(() => { loadSettings(); loadLog() }, [])

  const save = async () => {
    setLoading(true); setError(null)
    try {
      await apiService.updateEmailSettings({
        mode, test_recipient: testRecipient || null,
        cc_admins: ccAdmins, extra_cc: extraCc || null, notes: notes || null,
      })
      setSnack({ severity: 'success', msg: 'Ustawienia zapisane.' })
      await loadSettings()
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    } finally { setLoading(false) }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>Konfiguracja e-mail</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Tryb wysyłki dla raportów per Agent. <strong>SMTP</strong> czyta credentials z <code>.env</code>
        (<code>SMTP_HOST</code>, <code>SMTP_USER</code>, <code>SMTP_PASSWORD</code>, <code>SMTP_FROM</code>).
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Ustawienia" />
        <Tab label={`Historia wysyłek (${log.length})`} />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {tab === 0 && (
        <Paper variant="outlined" sx={{ p: 3, maxWidth: 720 }}>
          {loading && !settings ? <CircularProgress /> : (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Tryb wysyłki</Typography>
                <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)} row>
                  <FormControlLabel value="test" control={<Radio />} label="🧪 Tryb testowy" />
                  <FormControlLabel value="production" control={<Radio />} label="🚀 Produkcja" />
                </RadioGroup>
                {mode === 'test' ? (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Wszystkie wysyłki idą wyłącznie na <strong>adres testowy</strong> (poniżej).
                    Realni odbiorcy są tylko logowani.
                  </Alert>
                ) : (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    🚀 Produkcja — maile pójdą do <strong>realnych odbiorców</strong> wynikających z roli (KAM + RKS/agent).
                    Upewnij się, że <code>SMTP_*</code> w <code>.env</code> jest poprawne i sprawdziłeś podgląd.
                  </Alert>
                )}
              </Box>

              <TextField
                label="Adres testowy (gdzie idą wszystkie maile w trybie testowym)"
                size="small" fullWidth
                value={testRecipient}
                onChange={(e) => setTestRecipient(e.target.value)}
                placeholder="np. marcin.krezelewski@locon.pl"
                helperText={mode === 'test' && !testRecipient ? '⚠ wymagane w trybie testowym' : ''}
              />

              <Divider />

              <FormControlLabel
                control={<Switch checked={ccAdmins} onChange={(e) => setCcAdmins(e.target.checked)} />}
                label="Dodaj CC do administratorów (extra_cc poniżej)"
              />
              <TextField
                label="Dodatkowe CC (oddzielone przecinkami)"
                size="small" fullWidth
                value={extraCc} onChange={(e) => setExtraCc(e.target.value)}
                placeholder="kam-team@locon.pl, dyrekcja@locon.pl"
              />
              <TextField
                label="Notatki / instrukcje"
                size="small" fullWidth multiline minRows={2}
                value={notes} onChange={(e) => setNotes(e.target.value)}
              />

              <Divider />

              <Box>
                <Typography variant="caption" color="text.secondary">
                  SMTP w <code>.env</code>: {' '}
                  <Chip label={settings?.smtp_configured ? '✅ skonfigurowane' : '❌ brak SMTP_HOST'}
                        color={settings?.smtp_configured ? 'success' : 'error'} size="small" />
                  {settings?.dry_run_env && (
                    <> · <Chip label="EMAIL_DRY_RUN=true (nic się nie wyśle, tylko zapis w logu)" color="warning" size="small" /></>
                  )}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={loadSettings} disabled={loading}>Anuluj</Button>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={loading}>
                  {loading ? 'Zapisywanie…' : 'Zapisz'}
                </Button>
              </Stack>
            </Stack>
          )}
        </Paper>
      )}

      {tab === 1 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Ostatnie {log.length} wysyłek</Typography>
            <IconButton size="small" onClick={loadLog}><RefreshIcon /></IconButton>
          </Stack>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Operator</TableCell>
                  <TableCell>Raport</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell>Tryb</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Rzeczywiści odbiorcy</TableCell>
                  <TableCell>Realni (gdyby produkcja)</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {log.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{new Date(r.sent_at).toLocaleString('pl-PL')}</TableCell>
                    <TableCell>{r.operator_code}</TableCell>
                    <TableCell>{r.report_type}</TableCell>
                    <TableCell>{r.scope_key}</TableCell>
                    <TableCell>
                      <Chip size="small" label={r.mode_at_send}
                        color={r.mode_at_send === 'test' ? 'warning' : 'success'} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={r.status}
                        color={r.status === 'sent' ? 'success' : r.status === 'failed' ? 'error' : 'default'} />
                      {r.error_message && (
                        <Tooltip title={r.error_message}><span style={{ marginLeft: 4 }}>⚠</span></Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {(r.actual_recipients || []).map((x, i) => (
                        <div key={i} style={{ fontSize: 11 }}>{x.email}</div>
                      ))}
                    </TableCell>
                    <TableCell>
                      {(r.intended_recipients || []).map((x, i) => (
                        <div key={i} style={{ fontSize: 11, color: '#666' }}>
                          {x.role}: {x.email}
                        </div>
                      ))}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" component="a" target="_blank"
                        href={apiService.getEmailLogHtmlUrl(r.id)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {log.length === 0 && (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    Brak wysłanych raportów. Otwórz „Sprzedaż per Agent" i kliknij „Wyślij raport" przy agencie.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}

      <Snackbar
        open={!!snack} autoHideDuration={3500} onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)}>{snack.msg}</Alert> : null}
      </Snackbar>
    </Box>
  )
}

export default AdminEmailSettingsPage
